import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import ApiService from "@app/store/api/ApiService";
import { LockableDealRef } from "@app/util/deal/getDealLock";

export const fetchAllFunnels = createAsyncAction(
  "@deal/fetchAllFunnels/request",
  "@deal/fetchAllFunnels/success",
  "@deal/fetchAllFunnels/failure"
)<void, Funnel[], unknown>();

export const createFunnel = createAsyncAction(
  "@deal/createFunnel/request",
  "@deal/createFunnel/success",
  "@deal/createFunnel/failure"
)<{ callback?: (funnel: Funnel) => void; funnel: Partial<Omit<Funnel, "id">> }, Funnel, void>();

export const updateFunnel = createAsyncAction(
  "@deal/updateFunnel/request",
  "@deal/updateFunnel/success",
  "@deal/updateFunnel/failure"
)<{ funnel: Funnel }, Funnel, void>();

export const deleteFunnel = createAsyncAction(
  "@deal/deleteFunnel/request",
  "@deal/deleteFunnel/success",
  "@deal/deleteFunnel/failure"
)<Funnel, Funnel, void>();

export const ensureStagesFetched = createAsyncAction(
  "@deal/ensureStagesFetched/request",
  "@deal/ensureStagesFetched/success",
  "@deal/ensureStagesFetched/failure"
)<Funnel["id"][] | undefined, Record<Funnel["id"], Stage[]>, void>();

export const createStage = createAsyncAction(
  "@deal/createStage/request",
  "@deal/createStage/success",
  "@deal/createStage/failure"
)<{ funnelId: Funnel["id"]; stage: Partial<Omit<Stage, "id">> }, Stage, void>();

export const updateStages = createAsyncAction(
  "@deal/updateStages/request",
  "@deal/updateStages/success",
  "@deal/updateStages/failure"
)<{ funnelId: Funnel["id"]; stages: Stage[] }, { funnelId: Funnel["id"]; stages: Stage[] }, void>();

export const deleteStage = createAsyncAction(
  "@deal/deleteStage/request",
  "@deal/deleteStage/success",
  "@deal/deleteStage/failure"
)<Stage, Stage, void>();

export const updateDeal = createAsyncAction(
  "@deal/update/request",
  "@deal/update/success",
  "@deal/update/failure"
)<Deal, void, void>();

// WARNING! Only use this action when you can't get deal from the store
export const fetchDeal = createAction("@deal/fetchDeal")<{
  callback: (deal: Deal) => void;
  // False when the caller renders its own inline failure state. Defaults to true so
  // existing call sites keep the global toast they have always had.
  displayNotification?: boolean;
  // Passed the error so a caller can branch on its status — a 404/403 is a permanent
  // "you can't see this record", anything else is worth retrying.
  failureCallback?: (error?: unknown) => void;
  id: Deal["id"];
  options?: Parameters<ApiService["fetchDeal"]>[2];
}>();

// The whole ListResponse goes to the caller, not just `data` — a caller that shows
// "N open deals" needs `total`, which is the match count rather than the page size.
export const fetchDeals = createAction("@deal/fetchDeals")<{
  callback: (response: ListResponse<Deal>) => void;
  displayNotification?: boolean;
  failureCallback?: (error?: unknown) => void;
  options?: Parameters<ApiService["fetchDeals"]>[1];
}>();

// The funnel and stage of deals a screen knows only by id (a Field Agent record_update card), for
// judging their lock. A deal that can't be read is left out: the server stays the backstop.
export const fetchDealRefs = createAsyncAction(
  "@deal/fetchDealRefs/request",
  "@deal/fetchDealRefs/success",
  "@deal/fetchDealRefs/failure"
)<Deal["id"][], LockableDealRef[], void>();

export type Actions = ActionType<
  | typeof createFunnel
  | typeof createStage
  | typeof deleteFunnel
  | typeof deleteStage
  | typeof ensureStagesFetched
  | typeof fetchAllFunnels
  | typeof fetchDeal
  | typeof fetchDealRefs
  | typeof fetchDeals
  | typeof updateDeal
  | typeof updateFunnel
  | typeof updateStages
>;
